import * as React from 'react';
import { useCreate, useGetList, useGetOne, useNotify, useRedirect, useUpdate } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../../components/page-header/PageHeader';
import { FormBackButton } from '../../components/back-button/FormBackButton';
import { ScrollToFirstError } from '../../components/form/ScrollToFirstError';
import { Switch } from '../../components/switch/Switch';
import { labelAboveSx } from '../../core/theme/formStyles';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { preventNumberInputScroll } from '../../utils/preventNumberInputScroll';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';
import { useOwnRestaurant } from '../../hooks/useOwnRestaurant';
import { isAdminRole } from '../../utils/roles';
import { currencySymbol, DiscountType, formatMoney } from '../coupons/couponTypes';
import {
  Discount,
  DiscountScope,
  DiscountTimeWindow,
  hasOverlappingWindows,
  isValidWindow,
  toDiscountPayload,
} from './discountTypes';
import { DiscountScheduleEditor } from './DiscountScheduleEditor';

interface DiscountFormValues {
  locationId: string;
  name: string;
  discountType: DiscountType;
  discountValue: string;
  scope: DiscountScope;
  inventoryItemIds: number[];
  hasMinOrder: boolean;
  minOrderAmount: string;
  startDate: string;
  endDate: string;
  timeWindows: DiscountTimeWindow[];
  hasBudget: boolean;
  budget: string;
  firstOrderOnly: boolean;
  hasPerCustomerLimit: boolean;
  perCustomerLimit: string;
  isActive: boolean;
}

interface MenuItemOption {
  id: number;
  name: string;
  category?: string;
}

const MAX_AMOUNT = 10000;
const MAX_BUDGET = 1000000;
const MAX_PER_CUSTOMER_LIMIT = 1000;


const hasAtMostTwoDecimals = (value?: number) =>
  value == null ? true : Math.abs(Math.round(value * 100) - value * 100) < 1e-6;

const emptyToUndefined = (value: unknown, original: unknown) =>
  original === '' ? undefined : value;

// Ten sam uklad co CouponFormPage: jedna kolumna pol bez ramek sekcji. Grupy pol
// (rodzaj, zakres, harmonogram) maja etykiete jak "Rodzaj rabatu" w formularzu kuponu.
const groupLabelSx = {
  fontSize: 'var(--text-sm)',
  fontWeight: 500,
  color: 'text.secondary',
  mb: '3px',
};

const toggleRowSx = {
  gap: 'var(--space-4)',
  p: 'var(--space-3) var(--space-4)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

export default function DiscountFormPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();
  const [update] = useUpdate();
  const isAdmin = isAdminRole();
  const { locations, loading: locationsLoading } = useAccessibleLocations();
  const { restaurant } = useOwnRestaurant();

  const { data: discount, isLoading: discountLoading } = useGetOne<Discount>(
    'discounts',
    { id: Number(id) },
    { enabled: isEditMode },
  );
  const spentAmount = discount?.spentAmount ?? 0;

  const validationSchema = Yup.object({
    locationId: Yup.string().required(t('discounts.validation.locationRequired')),
    name: Yup.string()
      .trim()
      .required(t('discounts.validation.nameRequired'))
      .max(120, t('discounts.validation.nameMax')),
    discountType: Yup.string().oneOf(['PERCENT', 'AMOUNT']).required(),
    discountValue: Yup.number()
      .typeError(t('discounts.validation.valueRequired'))
      .required(t('discounts.validation.valueRequired'))
      .moreThan(0, t('discounts.validation.valuePositive'))
      .test('decimals', t('discounts.validation.decimals'), hasAtMostTwoDecimals)
      .when('discountType', {
        is: 'PERCENT',
        then: (schema) => schema.max(100, t('discounts.validation.percentMax')),
        otherwise: (schema) =>
          schema.max(MAX_AMOUNT, t('discounts.validation.amountMax', { max: MAX_AMOUNT })),
      }),
    scope: Yup.string().oneOf(['ALL_ITEMS', 'SELECTED_ITEMS']).required(),
    inventoryItemIds: Yup.array().when('scope', {
      is: 'SELECTED_ITEMS',
      then: (schema) => schema.min(1, t('discounts.validation.itemsRequired')),
    }),
    minOrderAmount: Yup.number()
      .transform(emptyToUndefined)
      .when('hasMinOrder', {
        is: true,
        then: (schema) =>
          schema
            .typeError(t('discounts.validation.amountInvalid'))
            .required(t('discounts.validation.minOrderRequired'))
            .moreThan(0, t('discounts.validation.valuePositive'))
            .test('decimals', t('discounts.validation.decimals'), hasAtMostTwoDecimals),
        otherwise: (schema) => schema.optional(),
      }),
    timeWindows: Yup.array()
      .test('valid-windows', t('discounts.scheduleEditor.invalidWindow'), (windows) =>
        ((windows ?? []) as DiscountTimeWindow[]).every(isValidWindow),
      )
      .test(
        'no-overlap',
        t('discounts.scheduleEditor.overlapping'),
        (windows) => !hasOverlappingWindows((windows ?? []) as DiscountTimeWindow[]),
      ),
    endDate: Yup.string().test(
      'after-start',
      t('discounts.validation.dateRange'),
      function (value) {
        const { startDate } = this.parent as DiscountFormValues;
        return !value || !startDate || value >= startDate;
      },
    ),
    budget: Yup.number()
      .transform(emptyToUndefined)
      .when('hasBudget', {
        is: true,
        then: (schema) =>
          schema
            .typeError(t('discounts.validation.amountInvalid'))
            .required(t('discounts.validation.budgetRequired'))
            .moreThan(0, t('discounts.validation.valuePositive'))
            .max(MAX_BUDGET, t('discounts.validation.budgetMax'))
            .test('decimals', t('discounts.validation.decimals'), hasAtMostTwoDecimals)
            .test('above-spent', function (value) {
              const { isActive } = this.parent as DiscountFormValues;
              if (!isActive || value == null || value > spentAmount) return true;
              return this.createError({ message: t('discounts.validation.budgetBelowSpent') });
            }),
        otherwise: (schema) => schema.optional(),
      }),
    perCustomerLimit: Yup.number()
      .transform(emptyToUndefined)
      .when('hasPerCustomerLimit', {
        is: true,
        then: (schema) =>
          schema
            .typeError(t('discounts.validation.perCustomerLimitRange'))
            .required(t('discounts.validation.perCustomerLimitRequired'))
            .integer(t('discounts.validation.perCustomerLimitRange'))
            .min(1, t('discounts.validation.perCustomerLimitRange'))
            .max(MAX_PER_CUSTOMER_LIMIT, t('discounts.validation.perCustomerLimitRange')),
        otherwise: (schema) => schema.optional(),
      }),
    // Przelaczniki sie wzajemnie blokuja, ale backend i tak odrzuci oba naraz (400) -
    // pilnujemy tego tez tutaj, zeby blad nie przyszedl dopiero po zapisie.
    firstOrderOnly: Yup.boolean().test(
      'customer-rules-conflict',
      t('discounts.validation.customerRulesConflict'),
      function (value) {
        const { hasPerCustomerLimit } = this.parent as DiscountFormValues;
        return !(value && hasPerCustomerLimit);
      },
    ),
  });

  const defaultLocationId = isAdmin
    ? locations.length === 1
      ? String(locations[0].id)
      : ''
    : restaurant
      ? String(restaurant.id)
      : '';

  const initialValues: DiscountFormValues =
    isEditMode && discount
      ? {
          locationId: String(discount.locationId),
          name: discount.name,
          discountType: discount.discountType,
          discountValue: String(discount.discountValue),
          scope: discount.scope,
          inventoryItemIds: discount.inventoryItemIds,
          hasMinOrder: discount.minOrderAmount > 0,
          minOrderAmount: discount.minOrderAmount > 0 ? String(discount.minOrderAmount) : '',
          startDate: discount.startDate ?? '',
          endDate: discount.endDate ?? '',
          timeWindows: discount.timeWindows,
          hasBudget: discount.budget != null,
          budget: discount.budget != null ? String(discount.budget) : '',
          firstOrderOnly: discount.firstOrderOnly ?? false,
          hasPerCustomerLimit: discount.perCustomerLimit != null,
          perCustomerLimit:
            discount.perCustomerLimit != null ? String(discount.perCustomerLimit) : '',
          isActive: discount.isActive,
        }
      : {
          locationId: defaultLocationId,
          name: '',
          discountType: 'PERCENT',
          discountValue: '',
          scope: 'ALL_ITEMS',
          inventoryItemIds: [],
          hasMinOrder: false,
          minOrderAmount: '',
          startDate: '',
          endDate: '',
          timeWindows: [],
          hasBudget: false,
          budget: '',
          firstOrderOnly: false,
          hasPerCustomerLimit: false,
          perCustomerLimit: '',
          isActive: true,
        };

  const handleSubmit = async (values: DiscountFormValues) => {
    const data = toDiscountPayload({
      locationId: Number(values.locationId),
      name: values.name.trim(),
      discountType: values.discountType,
      discountValue: Number(values.discountValue),
      scope: values.scope,
      inventoryItemIds: values.inventoryItemIds,
      // Wylaczony przelacznik = bez progu (backend: 0), nawet jesli w polu zostala kwota.
      minOrderAmount: values.hasMinOrder
        ? Math.round(Number(values.minOrderAmount) * 100) / 100
        : 0,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
      timeWindows: values.timeWindows,
      budget: values.hasBudget ? Math.round(Number(values.budget) * 100) / 100 : null,
      firstOrderOnly: values.firstOrderOnly,
      // Wylaczony przelacznik = bez limitu (null), nawet jesli w polu zostala liczba.
      perCustomerLimit: values.hasPerCustomerLimit ? Number(values.perCustomerLimit) : null,
      isActive: values.isActive,
    });
    try {
      if (isEditMode && discount) {
        await update(
          'discounts',
          { id: discount.id, data, previousData: discount },
          { returnPromise: true },
        );
        notify(t('discounts.notifications.updated'), { type: 'success' });
      } else {
        await create('discounts', { data }, { returnPromise: true });
        notify(t('discounts.notifications.created'), { type: 'success' });
      }
      redirect('list', 'discounts');
    } catch (error: any) {
      notify(error?.body?.error ?? error?.message, { type: 'error' });
      Sentry.captureException(error);
    }
  };

  if (locationsLoading || (isEditMode && discountLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 'var(--space-16)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, setFieldValue, errors, touched, isSubmitting }) => {
        const selectedLocation = locations.find((l) => String(l.id) === values.locationId);
        const currency = selectedLocation?.currency;
        const unit = values.discountType === 'PERCENT' ? '%' : currencySymbol(currency);

        return (
          <Form>
            <ScrollToFirstError />
            <PageHeader
              back={<FormBackButton />}
              title={isEditMode ? t('discounts.editDiscount') : t('discounts.addDiscount')}
              subtitle={isEditMode ? discount?.name : undefined}
            />
            <Box sx={{ px: PAGE_GUTTER, py: { xs: 2, md: 4 }, maxWidth: 640, ...labelAboveSx }}>
              <Stack spacing={2.5}>
                {discount?.status === 'BUDGET_EXHAUSTED' && (
                  <Alert severity="warning">{t('discounts.form.exhaustedNotice')}</Alert>
                )}
                {isAdmin && (
                  <FormControl
                    size="small"
                    fullWidth
                    error={touched.locationId && Boolean(errors.locationId)}
                  >
                    <InputLabel shrink>{t('discounts.fields.location')}</InputLabel>
                    <Select
                      displayEmpty
                      name="locationId"
                      value={values.locationId}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue('inventoryItemIds', []);
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('discounts.fields.locationPlaceholder')}
                      </MenuItem>
                      {locations.map((l) => (
                        <MenuItem key={l.id} value={String(l.id)}>
                          {l.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched.locationId && errors.locationId ? errors.locationId : ' '}
                    </FormHelperText>
                  </FormControl>
                )}

                <TextField
                  name="name"
                  size="small"
                  fullWidth
                  label={t('discounts.fields.name')}
                  placeholder={t('discounts.fields.namePlaceholder')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    touched.name && errors.name ? errors.name : t('discounts.fields.nameHint')
                  }
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 120 } }}
                />

                <FormControl>
                  <Typography sx={groupLabelSx}>{t('discounts.fields.discountType')}</Typography>
                  <RadioGroup
                    row
                    name="discountType"
                    value={values.discountType}
                    onChange={(e) => setFieldValue('discountType', e.target.value as DiscountType)}
                  >
                    <FormControlLabel
                      value="PERCENT"
                      control={<Radio size="small" />}
                      label={t('discounts.fields.discountPercent')}
                    />
                    <FormControlLabel
                      value="AMOUNT"
                      control={<Radio size="small" />}
                      label={t('discounts.fields.discountAmount')}
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  name="discountValue"
                  type="number"
                  size="small"
                  fullWidth
                  label={t('discounts.fields.discountValue')}
                  placeholder={values.discountType === 'PERCENT' ? '25' : '15.00'}
                  value={values.discountValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onWheel={preventNumberInputScroll}
                  error={touched.discountValue && Boolean(errors.discountValue)}
                  helperText={
                    touched.discountValue && errors.discountValue
                      ? errors.discountValue
                      : values.discountType === 'AMOUNT'
                        ? t(
                            values.scope === 'ALL_ITEMS'
                              ? 'discounts.fields.amountHintOrder'
                              : 'discounts.fields.amountHintItem',
                          )
                        : ' '
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      min: 0,
                      step: values.discountType === 'PERCENT' ? 1 : 0.01,
                      inputMode: 'decimal',
                    },
                    input: {
                      endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                    },
                  }}
                />

                <FormControl>
                  <Typography sx={groupLabelSx}>{t('discounts.fields.scope')}</Typography>
                  <RadioGroup
                    row
                    name="scope"
                    value={values.scope}
                    onChange={(e) => setFieldValue('scope', e.target.value as DiscountScope)}
                  >
                    <FormControlLabel
                      value="ALL_ITEMS"
                      control={<Radio size="small" />}
                      label={t('discounts.scope.allItems')}
                    />
                    <FormControlLabel
                      value="SELECTED_ITEMS"
                      control={<Radio size="small" />}
                      label={t('discounts.scope.selected')}
                    />
                  </RadioGroup>
                </FormControl>
                {values.scope === 'SELECTED_ITEMS' && (
                  <ProductPicker
                    locationId={values.locationId}
                    value={values.inventoryItemIds}
                    onChange={(ids) => setFieldValue('inventoryItemIds', ids)}
                    error={
                      touched.inventoryItemIds && typeof errors.inventoryItemIds === 'string'
                        ? errors.inventoryItemIds
                        : undefined
                    }
                  />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <Typography sx={groupLabelSx}>{t('discounts.fields.schedule')}</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 'var(--space-4)',
                    }}
                  >
                    <TextField
                      name="startDate"
                      type="date"
                      size="small"
                      fullWidth
                      label={t('discounts.fields.startDate')}
                      value={values.startDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={t('discounts.fields.dateOptional')}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                      name="endDate"
                      type="date"
                      size="small"
                      fullWidth
                      label={t('discounts.fields.endDate')}
                      value={values.endDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.endDate && Boolean(errors.endDate)}
                      helperText={
                        touched.endDate && errors.endDate
                          ? errors.endDate
                          : t('discounts.fields.dateOptional')
                      }
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { min: values.startDate || undefined },
                      }}
                    />
                  </Box>
                  <DiscountScheduleEditor
                    value={values.timeWindows}
                    onChange={(windows) => setFieldValue('timeWindows', windows)}
                    timeZone={discount?.locationTimeZone}
                    error={typeof errors.timeWindows === 'string' ? errors.timeWindows : undefined}
                  />
                </Box>

                <Stack
                  component="label"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={toggleRowSx}
                >
                  <Box>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                      {t('discounts.fields.hasMinOrder')}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                      {t('discounts.fields.hasMinOrderHint')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={values.hasMinOrder}
                    onChange={(e) => setFieldValue('hasMinOrder', e.target.checked)}
                    inputProps={{ 'aria-label': t('discounts.fields.hasMinOrder') }}
                  />
                </Stack>
                {values.hasMinOrder && (
                  <TextField
                    name="minOrderAmount"
                    type="number"
                    size="small"
                    fullWidth
                    label={t('discounts.fields.minOrderAmount')}
                    placeholder="100.00"
                    value={values.minOrderAmount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={preventNumberInputScroll}
                    error={touched.minOrderAmount && Boolean(errors.minOrderAmount)}
                    helperText={
                      touched.minOrderAmount && errors.minOrderAmount ? errors.minOrderAmount : ' '
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { min: 0, step: 0.01, inputMode: 'decimal' },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">{currencySymbol(currency)}</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}

                <Stack
                  component="label"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={toggleRowSx}
                >
                  <Box>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                      {t('discounts.fields.hasBudget')}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                      {t('discounts.fields.hasBudgetHint')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={values.hasBudget}
                    onChange={(e) => setFieldValue('hasBudget', e.target.checked)}
                    inputProps={{ 'aria-label': t('discounts.fields.hasBudget') }}
                  />
                </Stack>
                {values.hasBudget && (
                  <TextField
                    name="budget"
                    type="number"
                    size="small"
                    fullWidth
                    label={t('discounts.fields.budget')}
                    placeholder="1000.00"
                    value={values.budget}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={preventNumberInputScroll}
                    error={touched.budget && Boolean(errors.budget)}
                    helperText={
                      touched.budget && errors.budget
                        ? errors.budget
                        : isEditMode && discount?.budget != null
                          ? t('discounts.fields.budgetUsed', {
                              spent: formatMoney(spentAmount, currency),
                              budget: formatMoney(discount.budget, currency),
                            })
                          : ' '
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { min: 0, step: 0.01, inputMode: 'decimal' },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">{currencySymbol(currency)}</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <Typography sx={groupLabelSx}>{t('discounts.fields.customerRules')}</Typography>
                  {/* Jedno ograniczenie naraz: wlaczony przelacznik blokuje drugi (jak backend). */}
                  <Stack
                    component="label"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      ...toggleRowSx,
                      ...(values.hasPerCustomerLimit && { cursor: 'default' }),
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 'var(--text-sm)',
                          color: values.hasPerCustomerLimit
                            ? 'var(--color-text-disabled)'
                            : 'text.primary',
                        }}
                      >
                        {t('discounts.fields.firstOrderOnly')}
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                        {t('discounts.fields.firstOrderOnlyHint')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={values.firstOrderOnly}
                      disabled={values.hasPerCustomerLimit}
                      onChange={(e) => setFieldValue('firstOrderOnly', e.target.checked)}
                      inputProps={{ 'aria-label': t('discounts.fields.firstOrderOnly') }}
                    />
                  </Stack>
                  <Stack
                    component="label"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      ...toggleRowSx,
                      ...(values.firstOrderOnly && { cursor: 'default' }),
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 'var(--text-sm)',
                          color: values.firstOrderOnly
                            ? 'var(--color-text-disabled)'
                            : 'text.primary',
                        }}
                      >
                        {t('discounts.fields.hasPerCustomerLimit')}
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                        {t('discounts.fields.hasPerCustomerLimitHint')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={values.hasPerCustomerLimit}
                      disabled={values.firstOrderOnly}
                      onChange={(e) => setFieldValue('hasPerCustomerLimit', e.target.checked)}
                      inputProps={{ 'aria-label': t('discounts.fields.hasPerCustomerLimit') }}
                    />
                  </Stack>
                  {values.hasPerCustomerLimit && (
                    <TextField
                      name="perCustomerLimit"
                      type="number"
                      size="small"
                      fullWidth
                      label={t('discounts.fields.perCustomerLimit')}
                      placeholder="3"
                      value={values.perCustomerLimit}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onWheel={preventNumberInputScroll}
                      error={touched.perCustomerLimit && Boolean(errors.perCustomerLimit)}
                      helperText={
                        touched.perCustomerLimit && errors.perCustomerLimit
                          ? errors.perCustomerLimit
                          : ' '
                      }
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: {
                          min: 1,
                          max: MAX_PER_CUSTOMER_LIMIT,
                          step: 1,
                          inputMode: 'numeric',
                        },
                      }}
                    />
                  )}
                  {errors.firstOrderOnly && (
                    <FormHelperText error>{errors.firstOrderOnly}</FormHelperText>
                  )}
                </Box>

                <Stack
                  component="label"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={toggleRowSx}
                >
                  <Box>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                      {t('discounts.fields.isActive')}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                      {t('discounts.fields.isActiveHint')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={values.isActive}
                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    inputProps={{ 'aria-label': t('discounts.fields.isActive') }}
                  />
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 'var(--space-3)',
                    pt: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => redirect('list', 'discounts')}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      borderColor: 'var(--color-error)',
                      color: 'var(--color-error)',
                      '&:hover': {
                        borderColor: 'var(--color-error-hover)',
                        backgroundColor: 'var(--color-error-soft)',
                        color: 'var(--color-error-hover)',
                      },
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-text-inverse)',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-hover)',
                        color: 'var(--color-text-inverse)',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={25} sx={{ color: 'white' }} />
                    ) : isEditMode ? (
                      t('common.saveChanges')
                    ) : (
                      t('common.add')
                    )}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

interface ProductPickerProps {
  locationId: string;
  value: number[];
  onChange: (ids: number[]) => void;
  error?: string;
}

// Produkty wybranej lokalizacji (ten sam endpoint co lista menu w panelu).
const ProductPicker = ({ locationId, value, onChange, error }: ProductPickerProps) => {
  const { t } = useTranslation();
  const { data = [], isLoading } = useGetList<MenuItemOption>(
    'itemsManagement',
    {
      filter: { restaurantId: locationId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' },
    },
    { enabled: Boolean(locationId) },
  );

  const selected = data.filter((item) => value.includes(Number(item.id)));

  // Wybrane produkty pod polem, jak "Powiazane produkty" w formularzu produktu - przy
  // kilku pozycjach tagi w samym polu zjadaly miejsce na wpisywanie.
  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={data}
        loading={isLoading}
        disabled={!locationId}
        value={selected}
        groupBy={(option) => option.category ?? ''}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        onChange={(_, items) => onChange(items.map((item) => Number(item.id)))}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('discounts.fields.products')}
            placeholder={t('discounts.fields.productsPlaceholder')}
            error={Boolean(error)}
            helperText={
              error ?? (locationId ? undefined : t('discounts.fields.productsNeedLocation'))
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
      />

      {selected.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {selected.map((item) => (
            <Chip
              key={item.id}
              label={item.name}
              size="small"
              onDelete={() => onChange(value.filter((id) => id !== Number(item.id)))}
              sx={{
                backgroundColor: 'var(--color-primary)',
                color: '#fff',
                '& .MuiChip-deleteIcon': {
                  color: '#fff',
                  '&:hover': { color: '#ddd' },
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
